import { MenuProps } from "antd";
import { useSelector } from "react-redux";
import { useGetOrganization } from "../../../../../api/v1";
import { Booking } from "../../../../../types/bookings";
import { isResidentialUsageType } from "../../../../libs/utils";

type LeaseActionDropdownData = {
  isLeaseActionDropdownVisible: boolean;
  items: MenuProps["items"];
  leaseType?: Booking["leaseType"];
};

export const useLeaseActionDropdown = ({ renewal }: { renewal?: boolean }): LeaseActionDropdownData => {
  const { organization } = useGetOrganization();
  const { bookingDetails } = useSelector((state: any) => state.newBooking);
  const { propertyDetails, leaseStatusHistory, leaseStatus, leaseType, earlyTerminationRequestStatus } =
    (bookingDetails || {}) as Booking;

  const isResidentialUsage = isResidentialUsageType(propertyDetails?.usagesType);
  const hasDepositPaid = leaseStatusHistory?.some((history) => history?.status === "deposit_paid") ?? false;
  const isRenewalLease = leaseType === "renewal" || leaseType === "extension" || renewal;
  const isActiveLease = leaseStatus === "active";
  const canTerminateLease = leaseStatus === "active" || leaseStatus === "lease_expired";

  const canAmendLease = isResidentialUsage && !isActiveLease && organization?.enableLeaseAmendment === true;

  const leaseAmendmentsItems: MenuProps["items"] = [
    ...(canAmendLease
      ? [
          { key: "TenantAmendments", label: "Tenant Detail Amendment" },
          { key: "leaseTermAmendment", label: "Lease Term Amendments" },
        ]
      : []),
    ...(canAmendLease && !hasDepositPaid
      ? [{ key: "paymentScheduleAmendments", label: "Payment Schedule Amendments" }]
      : []),
    ...(canAmendLease && !hasDepositPaid && !isRenewalLease
      ? [{ key: "paymentPlanAmendments", label: "Payment Plan Amendments" }]
      : []),
  ];

  const items: MenuProps["items"] = [
    ...(canTerminateLease ? [{ key: "terminate", label: "Terminate Lease" }] : []),
    ...leaseAmendmentsItems,
  ];

  const isTerminationRequestPendingOrApproved = ["pending", "approved"].includes(earlyTerminationRequestStatus);
  const isLeaseDraftOrCancelled = ["draft", "booking_cancelled"].includes(leaseStatus);
  const hasItems = (items?.length ?? 0) > 0;

  const isLeaseActionDropdownVisible =
    Boolean(bookingDetails) && !isTerminationRequestPendingOrApproved && !isLeaseDraftOrCancelled && hasItems;

  return { isLeaseActionDropdownVisible, items };
};
